import React from 'react';
import { Globe, Edit2, Trash2, ExternalLink, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { Site } from './types';
import { formatDate } from './utils';

interface SitesTableProps {
  sites: Site[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onEdit: (site: Site) => void;
  onDelete: (site: Site) => void;
  isSubmitting: boolean;
}

export const SitesTable: React.FC<SitesTableProps> = ({
  sites,
  searchTerm,
  onSearchChange,
  onEdit,
  onDelete,
  isSubmitting,
}) => (
  <div className="card">
    <div className="p-6 border-b border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">All Sites</h2>
          <p className="text-gray-600">View and manage site domains</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={18}
        />
        <input
          type="text"
          placeholder="Search by site name, subdomain, custom domain, or owner..."
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          className="pl-10 input-field w-full"
        />
      </div>
    </div>

    {/* Sites List */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Site Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Owner
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Custom Domain
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sites.map(site => (
            <tr key={site.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-gray-900">{site.name}</div>
                  {site.isLandingPage && (
                    <span
                      title="Designated as the Compete Pages landing page"
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-800 border border-purple-200"
                    >
                      Landing
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">{formatDate(site.createdAt)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{site.userName}</div>
                <div className="text-xs text-gray-500">{site.userEmail}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {site.customDomain ? (
                  <a
                    href={`https://${site.customDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                  >
                    <span>{site.customDomain}</span>
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-sm text-gray-400 italic">None</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    site.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  )}
                >
                  {site.isPublished ? 'Published' : 'Draft'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onEdit(site)}
                    className="text-blue-600 hover:text-blue-700"
                    title="Edit site"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => onDelete(site)}
                    disabled={isSubmitting}
                    className="text-red-600 hover:text-red-700"
                    title="Delete site"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sites.length === 0 && (
        <div className="text-center py-12">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No sites found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'No sites have been created yet.'}
          </p>
        </div>
      )}
    </div>
  </div>
);
